import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, type ChannelStream, type Device, type Position, type RecordingClip, type RecordingList, type RecordingStartResult, type RecordingStreamInfo } from "../api";
import { positionAtTime, trailUpTo, type GpsAtTime, type GpsSample } from "../lib/playback-gps";
import {
  formatKathmanduWhen,
  kathmanduDateInputValue,
  kathmanduTimeInputValue,
  rangeFromKathmandu,
} from "../lib/nepal-time";
import { VideoPlayer, type PlayerStatus } from "./VideoPlayer";
import { PlaybackMap } from "./PlaybackMap";

const SPEEDS = [1, 2, 4, 8] as const;
const PLAY_WINDOW_MS = 10 * 60 * 1000;
/**
 * Client abort for Search → refresh=1 (0x9205). Must exceed api-server
 * QUERY_TIMEOUT_MS so we receive the server's timeout/cache fallback JSON
 * instead of a canceled fetch (Network "(canceled)" ~45s).
 */
const SEARCH_TIMEOUT_MS = 65_000;
/**
 * How long /stream keeps polling after arm. The player attaches immediately and
 * buffers; this only refreshes pipeline state. It must not declare failure.
 */
const PLAYBACK_POLL_MS = 3 * 60_000;
/** Client abort for recordings/start — must cover ACK + live-port fallback + packet gate. */
const START_TIMEOUT_MS = 150_000;
const PLAYBACK_FAIL_MSG = "Recording found, but playback could not be started";
const NO_RECORDING_MSG = "No recording found";
const UNAVAILABLE_MSG = "Recording unavailable";
/** If the player never decodes a frame, leave Loading. The backend already waited for video. */
const PLAYBACK_PICTURE_DEADLINE_MS = 75_000;
const STARTING_MSG = "Requesting recording…";
const WAITING_MSG = "Waiting for vehicle…";
const PREPARING_MSG = "Preparing video…";
const READY_MSG = "Playback ready";

export type PlaybackFix = {
  deviceId: string;
  position: GpsAtTime;
  trail: Array<[number, number]>;
  gap: boolean;
  recordedAt: string;
};

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function formatClock(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "00:00:00";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function formatWhen(iso: string): string {
  return formatKathmanduWhen(iso);
}

function channelLabel(channelNo: number): string {
  if (channelNo === 1) return "Front Camera";
  if (channelNo === 2) return "Rear Camera";
  return `Camera ${channelNo}`;
}

function playbackStream(serial: string, channel_no: number, hlsGen?: string): ChannelStream {
  const safe = String(serial ?? "").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 64) || "device";
  const gen = hlsGen && /^[a-f0-9]{8}$/i.test(hlsGen) ? `/${hlsGen.toLowerCase()}` : "";
  const path = `devices/${safe}/pb/ch${channel_no}${gen}`;
  return {
    channel_no,
    mediamtx_path: path,
    hlsUrl: `/hls/${path}/index.m3u8?cookieCheck=1`,
    webrtcUrl: "",
    // Playback ffmpeg muxes G.711→AAC when the camera sends audio.
    has_audio: true,
    live: false,
    waiting: true,
  };
}

function streamForPlayback(
  serial: string,
  channel_no: number,
  started?: { hlsGen?: string | null; hlsResults?: Array<{ channel: number; pbPath?: string }> },
): ChannelStream {
  const row = started?.hlsResults?.find((item) => item.channel === channel_no && item.pbPath);
  if (row?.pbPath) {
    return {
      channel_no,
      mediamtx_path: row.pbPath,
      hlsUrl: `/hls/${row.pbPath}/index.m3u8?cookieCheck=1`,
      webrtcUrl: "",
      has_audio: true,
      live: false,
      waiting: true,
    };
  }
  return playbackStream(serial, channel_no, started?.hlsGen ?? undefined);
}

function channelPipelineHint(pipeline: Record<string, unknown> | null, channel: number): string | null {
  if (!pipeline) return null;
  const row = pipeline[String(channel)] as Record<string, unknown> | undefined;
  if (!row) return null;
  const err = typeof row.error === "string" ? row.error : null;
  if (err === "terminal_offline" || err === "transport_none") {
    return "Camera offline on JT808 — 0x9201 was not delivered";
  }
  if (err === "not_authenticated") return "Camera not authenticated on JT808 yet";
  if (err) return `Playback failed (${err})`;
  if (row.delivered === false) return "0x9201 was not delivered to the camera";
  const ack = row.ack;
  const packets = Number(row.packets ?? row.packetCount ?? 0);
  if (ack != null && Number(ack) !== 0) return `Camera rejected playback (ACK ${ack})`;
  if (row.fileStartAt == null && row.fileEndAt == null) return "No memory-card file for this channel";
  if (Number.isFinite(packets) && packets <= 0 && row.ack === 0) return "Camera ACKed but sent no historical video yet";
  if (row.hlsStatus === 404 || row.hlsStatus === 500) {
    if (Number.isFinite(packets) && packets <= 0) {
      return "No JT1078 packets on /pb — camera did not publish historical video";
    }
    return "Waiting for /pb HLS muxer…";
  }
  return null;
}

function channelDefinitivelyFailed(pipeline: Record<string, unknown> | null, channel: number): boolean {
  if (!pipeline) return false;
  const row = pipeline[String(channel)] as Record<string, unknown> | undefined;
  if (!row) return false;
  const err = typeof row.error === "string" ? row.error : null;
  if (err === "terminal_offline" || err === "transport_none" || err === "not_authenticated") return true;
  if (row.delivered === false) return true;
  if (row.ack != null && Number(row.ack) !== 0) return true;
  if (row.fileStartAt == null && row.fileEndAt == null && "fileStartAt" in row) return true;
  return false;
}

function clipHasChannel(clip: RecordingClip, channel: number): boolean {
  return clip.channels.some((c) => c.channel_no === channel);
}

function exportSpec(device: Device, clip: RecordingClip, channel: number) {
  const file = clip.channels.find((row) => row.channel_no === channel);
  return {
    deviceId: device.id,
    channel,
    cameraLabel: channelLabel(channel),
    deviceLabel: device.label || device.serial_no,
    startAt: file?.start_at ?? clip.start_at,
    endAt: file?.end_at ?? clip.end_at,
  };
}

function windowAround(clip: RecordingClip, offsetMs: number, searchStartIso?: string): { startAt: string; endAt: string } {
  // Timeline offset is relative to Search start when provided; clip bounds still
  // clamp the play window so we never request media past card files.
  const origin = Date.parse(searchStartIso || clip.start_at);
  const clipEnd = Date.parse(clip.end_at);
  const start = Math.max(Date.parse(clip.start_at), Math.min(clipEnd - 1000, origin + Math.max(0, offsetMs)));
  const end = Math.min(clipEnd, start + PLAY_WINDOW_MS);
  return { startAt: new Date(start).toISOString(), endAt: new Date(Math.max(start + 1000, end)).toISOString() };
}

/** True when absolute playhead falls inside the actual SD recording (availability layer). */
function inActualRecording(absoluteMs: number, clip: RecordingClip | null): boolean {
  if (!clip || !Number.isFinite(absoluteMs)) return false;
  const start = Date.parse(clip.start_at);
  const end = Date.parse(clip.end_at);
  if (!(Number.isFinite(start) && Number.isFinite(end) && end > start)) return false;
  return absoluteMs >= start && absoluteMs < end - 250;
}

/** Active 0x1205 SD-file bounds — scrub uses 0x9202 inside; /start only outside. */
function sdFileWindowMs(
  files: Array<{ startAt: string; endAt: string }> | null | undefined,
): { startMs: number; endMs: number } | null {
  if (!files?.length) return null;
  const starts = files.map((f) => Date.parse(f.startAt)).filter((ms) => Number.isFinite(ms));
  const ends = files.map((f) => Date.parse(f.endAt)).filter((ms) => Number.isFinite(ms));
  if (starts.length === 0 || ends.length === 0) return null;
  return { startMs: Math.min(...starts), endMs: Math.max(...ends) };
}

/** Absolute playhead for Search: clamp the requested start into the clip. */
function playAtForClip(clip: RecordingClip, requestedIso?: string | null): string {
  const start = Date.parse(clip.start_at);
  const end = Date.parse(clip.end_at);
  const req = requestedIso ? Date.parse(requestedIso) : start;
  if (!(Number.isFinite(start) && Number.isFinite(end) && end > start)) return clip.start_at;
  const t = Number.isFinite(req) ? Math.max(start, Math.min(end - 1000, req)) : start;
  return new Date(t).toISOString();
}

function mergeRecordingList(prev: RecordingList | null, next: RecordingList): RecordingList {
  // A successful camera list is authoritative, including an empty one.
  if (next.source === "device") return next;
  if (next.clips.length > 0) return next;
  if (
    prev &&
    prev.clips.length > 0 &&
    prev.device_id === next.device_id &&
    prev.from === next.from &&
    prev.to === next.to
  ) {
    return {
      ...prev,
      source: prev.source === "device" ? "device" : "cache",
      message: next.message ?? prev.message,
      sent_9205: next.sent_9205 ?? prev.sent_9205,
    };
  }
  return next;
}

export function PlaybackPanel({
  device,
  embedMap,
  onClose,
  onFix,
  onSwitchDevice,
  closeLabel = "Back to live",
}: {
  device: Device;
  embedMap: boolean;
  onClose: () => void;
  onFix?: (fix: PlaybackFix | null) => void;
  onSwitchDevice?: (deviceId: string) => void;
  closeLabel?: string;
}) {
  const [startDate, setStartDate] = useState(() => kathmanduDateInputValue(Date.now()));
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState(() => kathmanduDateInputValue(Date.now()));
  const [endTime, setEndTime] = useState(() => kathmanduTimeInputValue(Date.now()));
  const [list, setList] = useState<RecordingList | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  /**
   * Timeline coordinate system = the Search Window the user submitted.
   * Set synchronously on Search click — never derived from clip length, HLS
   * duration, or a late list.from/list.to echo.
   */
  const [searchWindow, setSearchWindow] = useState<{ from: string; to: string } | null>(null);
  const [clip, setClip] = useState<RecordingClip | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [offsetMs, setOffsetMs] = useState(0);
  const [streams, setStreams] = useState<ChannelStream[]>([]);
  const [recoverNonce, setRecoverNonce] = useState(0);
  const [seekNonce, setSeekNonce] = useState(0);
  const [seekAttach, setSeekAttach] = useState(false);
  const [hlsGen, setHlsGen] = useState("");
  const hlsGenRef = useRef("");
  const [seekEpoch, setSeekEpoch] = useState(0);
  const [gpsPoints, setGpsPoints] = useState<GpsSample[]>([]);
  const [startError, setStartError] = useState<string | null>(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [playbackArmed, setPlaybackArmed] = useState(false);
  /** True while POST /recordings/start is in flight — never leave the tile on vague Loading. */
  const [startingPlayback, setStartingPlayback] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const [cameraSyncing, setCameraSyncing] = useState(false);
  const [muted, setMuted] = useState(true);
  const [waitExpired, setWaitExpired] = useState(false);
  const [waitEpoch, setWaitEpoch] = useState(0);
  const [frontStatus, setFrontStatus] = useState<PlayerStatus>("idle");
  const [rearStatus, setRearStatus] = useState<PlayerStatus>("idle");
  const [frontBuffering, setFrontBuffering] = useState(false);
  const [rearBuffering, setRearBuffering] = useState(false);
  /** Single recorded player — Front (1) or Rear (2). */
  const [activeChannel, setActiveChannel] = useState<1 | 2>(1);
  const activeChannelRef = useRef<1 | 2>(1);
  activeChannelRef.current = activeChannel;
  const [mediaBuffering, setMediaBuffering] = useState(false);
  const mediaBufferingRef = useRef(false);
  const [pipeline, setPipeline] = useState<Record<string, unknown> | null>(null);
  const pipelineRef = useRef<Record<string, unknown> | null>(null);
  pipelineRef.current = pipeline;
  /** Channels that actually received a 0x9201 file (may be a subset of the clip). */
  const [startedChannels, setStartedChannels] = useState<number[] | null>(null);
  const [ended, setEnded] = useState(false);
  const [seeking, setSeeking] = useState(false);
  /** Playhead is on the Search timeline but outside actual SD recording. */
  const [outsideRecording, setOutsideRecording] = useState(false);
  const retriedStart = useRef(false);
  const playingRef = useRef(false);
  const speedRef = useRef(1);
  const offsetRef = useRef(0);
  const lastTickRef = useRef(0);
  const clipRef = useRef<RecordingClip | null>(null);
  const listRef = useRef<RecordingList | null>(null);
  const searchWindowRef = useRef<{ from: string; to: string } | null>(null);
  const outsideRecordingRef = useRef(false);
  const seekAbort = useRef<AbortController | null>(null);
  const seekTimer = useRef<number | undefined>(undefined);
  const seekGen = useRef(0);
  const onFixRef = useRef(onFix);
  const mediaReadyRef = useRef(false);
  const anyReadyRef = useRef(false);
  const seekingRef = useRef(false);
  const waitStartedAt = useRef(0);
  const loadSeq = useRef(0);
  const searchAbort = useRef<AbortController | null>(null);
  const windowStartRef = useRef(0);
  const pendingSeekAt = useRef<string | null>(null);
  const windowEndRef = useRef(0);
  const extendingRef = useRef(false);
  const extendSliceRef = useRef<() => void>(() => undefined);
  const seekCommitRef = useRef<number | null>(null);
  const seekStreamReadyRef = useRef(false);
  const mediaSecRef = useRef(-1);
  const prefetchedEndRef = useRef(0);
  const [mediaAbsMs, setMediaAbsMs] = useState<number | null>(null);
  const [seekTargetMs, setSeekTargetMs] = useState<number | null>(null);
  const [seekStreamReady, setSeekStreamReady] = useState(false);
  const rearTimer = useRef(0);
  const startingRef = useRef(false);
  const selectClipRef = useRef<(next: RecordingClip, playAtIso?: string) => Promise<boolean>>(async () => false);
  const rangeRef = useRef({ startDate, startTime, endDate, endTime });
  rangeRef.current = { startDate, startTime, endDate, endTime };

  const applySdFileWindow = useCallback((files: Array<{ startAt: string; endAt: string }> | null | undefined) => {
    const win = sdFileWindowMs(files);
    if (!win) return;
    windowStartRef.current = win.startMs;
    windowEndRef.current = win.endMs;
  }, []);

  playingRef.current = playing;
  speedRef.current = speed;
  offsetRef.current = offsetMs;
  clipRef.current = clip;
  listRef.current = list;
  searchWindowRef.current = searchWindow;
  outsideRecordingRef.current = outsideRecording;
  onFixRef.current = onFix;
  mediaReadyRef.current = mediaReady;
  // Absolute recording clock: hold when seeking or the active tile buffers.
  const peerBufferHold =
    (activeChannel === 1 && frontStatus === "live" && frontBuffering) ||
    (activeChannel === 2 && rearStatus === "live" && rearBuffering);
  const syncHold = seeking || peerBufferHold;
  mediaBufferingRef.current = syncHold;
  seekingRef.current = seeking;

  useEffect(() => {
    // Peer buffer only — do not fold `seeking` into mediaBuffering or the
    // clear-seeking effect deadlocks (seeking ⇒ buffering ⇒ never clear).
    setMediaBuffering(peerBufferHold);
  }, [peerBufferHold]);

  // Timeline = Search Window only. Actual recording is an availability layer.
  const searchStartMs = searchWindow ? Date.parse(searchWindow.from) : Number.NaN;
  const searchEndMs = searchWindow ? Date.parse(searchWindow.to) : Number.NaN;
  const durationMs =
    Number.isFinite(searchStartMs) && Number.isFinite(searchEndMs) && searchEndMs > searchStartMs
      ? searchEndMs - searchStartMs
      : 0;
  const actualStartMs = clip ? Date.parse(clip.start_at) : Number.NaN;
  const actualEndMs = clip ? Date.parse(clip.end_at) : Number.NaN;
  const actualDurationMs =
    Number.isFinite(actualStartMs) && Number.isFinite(actualEndMs) && actualEndMs > actualStartMs
      ? actualEndMs - actualStartMs
      : 0;
  const actualBandLeftPct =
    durationMs > 0 && Number.isFinite(actualStartMs)
      ? Math.max(0, Math.min(100, ((actualStartMs - searchStartMs) / durationMs) * 100))
      : 0;
  const actualBandWidthPct =
    durationMs > 0 && Number.isFinite(actualStartMs) && Number.isFinite(actualEndMs)
      ? Math.max(0, Math.min(100 - actualBandLeftPct, ((actualEndMs - actualStartMs) / durationMs) * 100))
      : 0;
  // absoluteTarget = searchStart + timelinePosition — never video.currentTime / HLS.
  const playheadMs =
    Number.isFinite(searchStartMs) && durationMs > 0
      ? searchStartMs + Math.max(0, Math.min(durationMs, offsetMs))
      : 0;
  const pictureMs = !seeking && mediaAbsMs != null && mediaAbsMs > 0 ? mediaAbsMs : playheadMs;
  const gps = useMemo(() => (clip ? positionAtTime(gpsPoints, pictureMs) : null), [clip, gpsPoints, pictureMs]);
  const trail = useMemo(() => (clip ? trailUpTo(gpsPoints, pictureMs) : []), [clip, gpsPoints, pictureMs]);

  const setStartedChannelsStable = useCallback((next: number[] | null) => {
    setStartedChannels((prev) => {
      if (prev === next) return prev;
      if (prev && next && prev.length === next.length && prev.every((n, i) => n === next[i])) return prev;
      return next;
    });
  }, []);

  const resetPlaybackSession = useCallback(() => {
    setClip(null);
    setStreams([]);
    setStartError(null);
    setPlaying(false);
    playingRef.current = false;
    setSessionActive(false);
    setPlaybackArmed(false);
    setStartingPlayback(false);
    setMediaReady(false);
    mediaReadyRef.current = false;
    setMediaBuffering(false);
    mediaBufferingRef.current = false;
    setFrontBuffering(false);
    setRearBuffering(false);
    setEnded(false);
    setSeeking(false);
    setOutsideRecording(false);
    outsideRecordingRef.current = false;
    setOffsetMs(0);
    offsetRef.current = 0;
    setWaitExpired(false);
    setPipeline(null);
    setStartedChannelsStable(null);
    setGpsPoints([]);
    setFrontStatus("idle");
    setRearStatus("idle");
    setFrontBuffering(false);
    setRearBuffering(false);
    retriedStart.current = false;
  }, [setStartedChannelsStable]);

  /** Search only — never called on open, device select, or date/time edits. */
  const searchRecordings = useCallback(async () => {
    const seq = ++loadSeq.current;
    searchAbort.current?.abort();
    const ac = new AbortController();
    searchAbort.current = ac;
    const timer = window.setTimeout(() => ac.abort(), SEARCH_TIMEOUT_MS);

    const range = rangeFromKathmandu(
      rangeRef.current.startDate,
      rangeRef.current.startTime,
      rangeRef.current.endDate,
      rangeRef.current.endTime,
    );
    if ("error" in range) {
      window.clearTimeout(timer);
      setListError(range.error);
      setLoadingList(false);
      setCameraSyncing(false);
      return;
    }
    const { date, from, to } = range;
    const base = `/api/devices/${encodeURIComponent(device.id)}/recordings?date=${encodeURIComponent(date)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;

    setHasSearched(true);
    setListError(null);
    // Timeline coords lock here — before any async list/clip work.
    setSearchWindow({ from, to });
    searchWindowRef.current = { from, to };
    resetPlaybackSession();
    setSearchWindow({ from, to });
    searchWindowRef.current = { from, to };
    setLoadingList(true);
    setCameraSyncing(true);

    // Instant indexed results (no 0x9205). If that index already contains the
    // file for this window, start 0x9201 immediately. Only skip a live 0x9205
    // when playback actually started — a failed cache start must still refresh.
    const searchStarted = performance.now();
    let startedClipId: string | null = null;
    let playbackStarted = false;
    try {
      const cached = await api<RecordingList>(`${base}&cached=1&refresh=0`, { signal: ac.signal });
      if (seq !== loadSeq.current) return;
      if (cached.clips.length > 0) {
        setList(cached);
        const session = cached.clips[0] ?? null;
        if (session) {
          startedClipId = session.id;
          console.info("[PlaybackTiming] target_file_selected", {
            source: "cache",
            clipId: session.id,
            ms: Math.round(performance.now() - searchStarted),
          });
          playbackStarted = await selectClipRef.current(session, from);
        }
      }
    } catch {
      /* live query is authoritative when the index is empty */
    } finally {
      if (seq === loadSeq.current) setLoadingList(false);
    }

    // Skip live 0x9205 only when historical playback is already uploading —
    // a 0x9205 during 0x9201 freezes SD video on this terminal.
    if (playbackStarted) {
      if (seq === loadSeq.current) setCameraSyncing(false);
      return;
    }

    try {
      const live = await api<RecordingList>(`${base}&refresh=1`, { signal: ac.signal });
      if (seq !== loadSeq.current) return;
      const merged = mergeRecordingList(null, live);
      setList((prev) => mergeRecordingList(prev, live));
      const session = (live.clips[0] ?? merged.clips[0]) ?? null;
      if (session) {
        setListError(null);
        if (session.id !== startedClipId || !mediaReadyRef.current) {
          console.info("[PlaybackTiming] target_file_selected", {
            source: live.source === "device" ? "device" : "cache",
            clipId: session.id,
            ms: Math.round(performance.now() - searchStarted),
          });
          const ok = await selectClipRef.current(session, from);
          if (!ok && seq === loadSeq.current && !mediaReadyRef.current) {
            // Existence was confirmed (clip listed) but the camera did not play.
            // startRecording already set a clear startError — do not imply "no file".
          }
        }
      } else if (live.source === "device" || live.source === "cache") {
        // Authoritative empty list — recording does not exist for this window.
        setClip(null);
        setStreams([]);
        setSessionActive(false);
        setPlaybackArmed(false);
        setStartError(null);
        setListError(NO_RECORDING_MSG);
      } else if (live.source === "timeout" || live.source === "offline" || live.source === "error" || live.source === "unsupported") {
        if (startedClipId) {
          // Keep the indexed clip visible; playback may still be recoverable via Retry.
          setListError(live.message || "Camera did not answer in time. Click Search to retry.");
        } else {
          setListError(live.message || "Recording query failed. Click Search to retry.");
        }
      }
    } catch (err) {
      if (seq !== loadSeq.current) return;
      const aborted = err instanceof DOMException && err.name === "AbortError";
      if (aborted && (playbackStarted || mediaReadyRef.current)) return;
      setList((prev) => {
        if (prev && prev.clips.length > 0) return prev;
        return {
          device_id: device.id,
          source: aborted ? "timeout" : "error",
          queried_at: new Date().toISOString(),
          from,
          to,
          clips: [],
          message: aborted ? "Camera did not answer in time." : "Recording query failed.",
          sent_9205: true,
        };
      });
      if (playbackStarted || startedClipId) return;
      setListError(
        aborted
          ? "Camera did not answer in time. Click Search to retry."
          : err instanceof Error
            ? err.message
            : "Recording query failed. Click Search to retry.",
      );
    } finally {
      window.clearTimeout(timer);
      if (seq === loadSeq.current) {
        setCameraSyncing(false);
        setLoadingList(false);
      }
    }
  }, [device.id, resetPlaybackSession]);

  // Opening / switching camera clears state — does NOT query the card.
  useEffect(() => {
    searchAbort.current?.abort();
    loadSeq.current += 1;
    setList(null);
    setListError(null);
    setHasSearched(false);
    setSearchWindow(null);
    searchWindowRef.current = null;
    setLoadingList(false);
    setCameraSyncing(false);
    resetPlaybackSession();
  }, [device.id, resetPlaybackSession]);

  useEffect(() => {
    let raf = 0;
    const step = (now: number) => {
      const current = clipRef.current;
      const search = searchWindowRef.current;
      // Only advance the Search-window scrubber while a player is actually
      // decoding inside the actual recording — never while CONNECTING/seeking,
      // and never from HLS session currentTime.
      if (
        playingRef.current &&
        mediaReadyRef.current &&
        anyReadyRef.current &&
        !seekingRef.current &&
        !mediaBufferingRef.current &&
        !outsideRecordingRef.current &&
        current &&
        search
      ) {
        const searchStart = Date.parse(search.from);
        const searchEnd = Date.parse(search.to);
        const searchDur = Math.max(0, searchEnd - searchStart);
        const actualEnd = Date.parse(current.end_at);
        const mediaSec = mediaSecRef.current;
        const origin = windowStartRef.current;
        // The playhead follows the decoded picture (command start + video time).
        // A wall-clock tick ran ahead of the camera and made the timeline claim
        // a time the burned-in clock had not reached.
        if (!(mediaSec >= 0 && origin > 0)) {
          lastTickRef.current = 0;
        } else {
        const abs = origin + mediaSec * 1000;
        const next = Math.max(0, Math.min(searchDur, abs - searchStart));
        offsetRef.current = next;
        setOffsetMs(next);
        setMediaAbsMs(abs);
        const sliceEnd = windowEndRef.current;
        const remain = sliceEnd - abs;
        // Prepare the next slice while about a minute of this picture remains.
        if (remain < 90_000 && sliceEnd > prefetchedEndRef.current && sliceEnd < searchEnd - 2_000) {
          prefetchedEndRef.current = sliceEnd;
          extendSliceRef.current();
        }
        // Leaving the actual recording availability band → soft gap on the Search timeline.
        if (Number.isFinite(actualEnd) && abs >= actualEnd - 50) {
          outsideRecordingRef.current = true;
          setOutsideRecording(true);
          playingRef.current = false;
          setPlaying(false);
          setEnded(true);
        } else if (next >= searchDur - 50) {
          playingRef.current = false;
          setPlaying(false);
          setEnded(true);
        }
        }
      } else {
        lastTickRef.current = 0;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (!clip) {
      onFixRef.current?.(null);
      return;
    }
    if (!gps) return;
    onFixRef.current?.({
      deviceId: device.id,
      position: gps,
      trail,
      gap: gps.gap,
      recordedAt: new Date(pictureMs).toISOString(),
    });
  }, [clip, device.id, gps, pictureMs, trail]);

  useEffect(() => {
    if (!clip || !list) return;
    if (list.clips.some((row) => row.id === clip.id)) return;
    setClip(null);
    setStreams([]);
    setPlaying(false);
    setSessionActive(false);
  }, [clip, list]);

  useEffect(() => {
    const id = device.id;
    return () => {
      onFixRef.current?.(null);
      window.clearTimeout(rearTimer.current);
      void api(`/api/devices/${encodeURIComponent(id)}/recordings/stop`, { method: "POST", body: "{}" }).catch(() => undefined);
    };
  }, [device.id]);

  const streamPollGen = useRef(0);
  const streamPollAbort = useRef<AbortController | null>(null);
  const startedChannelsRef = useRef<number[] | null>(null);
  startedChannelsRef.current = startedChannels;
  const deviceSerialRef = useRef(device.serial_no);
  deviceSerialRef.current = device.serial_no;
  const deviceIdRef = useRef(device.id);
  deviceIdRef.current = device.id;

  const pollStream = useCallback(async () => {
    streamPollAbort.current?.abort();
    const ac = new AbortController();
    streamPollAbort.current = ac;
    const gen = streamPollGen.current;
    const serial = deviceSerialRef.current;
    const id = deviceIdRef.current;
    try {
      const info = await api<RecordingStreamInfo>(
        `/api/devices/${encodeURIComponent(id)}/recordings/stream`,
        { signal: ac.signal },
      );
      if (gen !== streamPollGen.current) return;
      const next = info.channels ?? [];
      if (info.pipeline) setPipeline(info.pipeline);
      if (seekingRef.current) return info;
      const wanted = new Set(
        (clipRef.current?.channels ?? [])
          .map((row) => row.channel_no)
          .concat(startedChannelsRef.current ?? [])
          .filter((n) => n >= 1),
      );
      setStreams((prev) => {
        const byCh = new Map(prev.map((ch) => [ch.channel_no, ch]));
        for (const ch of next) {
          if (wanted.size > 0 && !wanted.has(ch.channel_no)) continue;
          const apiPath = String(ch.mediamtx_path ?? "");
          const foundGen = apiPath.match(/\/pb\/ch\d+\/([a-f0-9]{8})$/i)?.[1]?.toLowerCase();
          if (foundGen && foundGen !== hlsGenRef.current) {
            hlsGenRef.current = foundGen;
            setHlsGen(foundGen);
          }
          const seeded = apiPath.includes("/pb/ch")
            ? {
                ...playbackStream(serial, ch.channel_no, hlsGenRef.current),
                mediamtx_path: apiPath,
                hlsUrl: ch.hlsUrl || `/hls/${apiPath}/index.m3u8?cookieCheck=1`,
              }
            : playbackStream(serial, ch.channel_no, hlsGenRef.current);
          const pipe = info.pipeline?.[String(ch.channel_no)] as {
            hlsHasSegments?: boolean;
            packets?: number;
            sessionId?: string;
          } | undefined;
          // Attach only when API reports playable /pb HLS (packets ∧ segments).
          // Packet counts alone caused index.m3u8 404 "no stream is available"
          // while ffmpeg never published to MediaMTX. Keep a prior live flag so
          // a brief probe flap cannot tear down an in-flight attach.
          const prevLive = Boolean(byCh.get(ch.channel_no)?.live);
          const ready =
            Boolean(ch.live) || Boolean(pipe?.hlsHasSegments) || prevLive;
          byCh.set(ch.channel_no, {
            ...ch,
            mediamtx_path: seeded.mediamtx_path,
            hlsUrl: seeded.hlsUrl || ch.hlsUrl,
            webrtcUrl: "",
            live: ready,
            waiting: !ready,
          });
        }
        for (const channel_no of wanted) {
          if (!byCh.has(channel_no)) byCh.set(channel_no, playbackStream(serial, channel_no, hlsGenRef.current));
        }
        return [...byCh.values()].sort((a, b) => a.channel_no - b.channel_no);
      });
      return info;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return null;
      const status = (err as { status?: number }).status;
      if (status === 401) {
        setStartError("Playback authentication failed");
        setSessionActive(false);
      }
      return null;
    }
  }, []);

  const anyReady = (activeChannel === 1 ? frontStatus : rearStatus) === "live";
  anyReadyRef.current = anyReady;
  const pipelineHasMedia = (() => {
    const row = pipeline?.[String(activeChannel)] as { hlsHasSegments?: boolean; packets?: number } | undefined;
    // Prefer playable HLS; packets alone are not enough to attach /pb.
    return Boolean(row?.hlsHasSegments) || Number(row?.packets ?? 0) > 0;
  })();

  useEffect(() => {
    // Poll only after 0x9201 was accepted (playbackArmed). Polling during /start
    // caused /stream storms whenever startedChannels got a new array identity.
    if (!clip || !sessionActive || !playbackArmed) {
      streamPollAbort.current?.abort();
      return;
    }
    if (startError && waitExpired) {
      streamPollAbort.current?.abort();
      return;
    }
    streamPollGen.current += 1;
    let cancelled = false;
    let attempt = 0;
    let timer = 0;
    const schedule = (ms: number) => {
      timer = window.setTimeout(run, ms);
    };
    const run = () => {
      if (cancelled) return;
      void pollStream().then((info) => {
        if (cancelled) return;
        // Stop polling once the player is live — further /stream storms hide nothing.
        if (anyReadyRef.current) return;
        const waited = waitStartedAt.current > 0 ? Date.now() - waitStartedAt.current : 0;
        if (waited > PLAYBACK_POLL_MS) return;
        void info;
        attempt += 1;
        // First segments often appear within a few seconds of 0x9201. Poll
        // quickly at first, then back off. Never a sub-300ms storm.
        const delay = Math.min(400 * 2 ** Math.min(attempt - 1, 4), 2_000);
        schedule(delay);
      });
    };
    run();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      streamPollAbort.current?.abort();
    };
  }, [clip?.id, sessionActive, playbackArmed, pollStream, startError, waitExpired]);

  useEffect(() => {
    // Arming is not a deadline by itself. If no picture is decoded, stop
    // showing Loading and surface the pipeline reason. Do not reset this
    // timer on /stream polls — that kept the tile on Loading forever.
    if (!clip || !sessionActive || !playbackArmed) return;
    setWaitExpired(false);
    retriedStart.current = false;
    const timer = window.setTimeout(() => {
      if (mediaReadyRef.current) return;
      const hint =
        channelPipelineHint(pipelineRef.current, activeChannelRef.current) ||
        "Playback did not start — the camera sent no playable video.";
      setStartError(hint);
      setWaitExpired(true);
      setPlaybackArmed(false);
      setStartingPlayback(false);
    }, PLAYBACK_PICTURE_DEADLINE_MS);
    return () => window.clearTimeout(timer);
  }, [clip, sessionActive, playbackArmed, waitEpoch]);

  // The playhead moves only after the replacement playlist decodes a frame.
  // A live status from the previous picture must not commit the scrub.
  useEffect(() => {
    if (!seeking || !seekStreamReady) return;
    const live = activeChannel === 1 ? frontStatus === "live" : rearStatus === "live";
    if (!live) return;
    const commit = seekCommitRef.current;
    if (commit != null) {
      setOffsetMs(commit);
      offsetRef.current = commit;
      lastTickRef.current = 0;
    }
    seekingRef.current = false;
    seekCommitRef.current = null;
    setSeekTargetMs(null);
    setSeeking(false);
  }, [seeking, seekStreamReady, frontStatus, rearStatus, activeChannel]);

  useEffect(() => {
    if (!seeking) return;
    const gen = seekGen.current;
    const failsafe = window.setTimeout(() => {
      if (gen !== seekGen.current) return;
      if (!seekingRef.current) return;
      seekingRef.current = false;
      seekStreamReadyRef.current = false;
      seekCommitRef.current = null;
      setSeekStreamReady(false);
      setSeekTargetMs(null);
      setSeeking(false);
    }, 90_000);
    return () => {
      window.clearTimeout(failsafe);
    };
  }, [seeking]);

  // Keep the active SD-file scrub window in sync when the server chains the next
  // 0x1205 file. Never expand this to the full search clip — that forced every
  // scrub through 0x9202 alone and could not jump into another card file.
  useEffect(() => {
    if (!pipeline || !startedChannels?.length) return;
    const files: Array<{ startAt: string; endAt: string }> = [];
    for (const ch of startedChannels) {
      const row = pipeline[String(ch)] as { fileStartAt?: string | null; fileEndAt?: string | null } | undefined;
      if (row?.fileStartAt && row?.fileEndAt) {
        files.push({ startAt: row.fileStartAt, endAt: row.fileEndAt });
      }
    }
    applySdFileWindow(files);
  }, [pipeline, startedChannels, applySdFileWindow]);

  // Segments on MediaMTX mean the pipeline is up — keep waiting and nudge the
  // player to attach. Do NOT start the playhead clock or window-extend loop
  // until a VideoPlayer reports live; otherwise offset races ahead, extend
  // re-POSTs /start every 2s, and each new 0x9201 kills the /pb publisher.
  const nudgedAttach = useRef(false);
  useEffect(() => {
    nudgedAttach.current = false;
  }, [clip?.id, sessionActive]);
  useEffect(() => {
    if (!clip || !sessionActive || mediaReady) return;
    if (anyReady) {
      setMediaReady(true);
      mediaReadyRef.current = true;
      setPlaying(true);
      playingRef.current = true;
      lastTickRef.current = 0;
      setStartError(null);
      return;
    }
    if (pipelineHasMedia && !nudgedAttach.current) {
      nudgedAttach.current = true;
      setStartError(null);
      console.info("[PlaybackTiming] playlist_ready", { at: new Date().toISOString() });
      setRecoverNonce((n) => n + 1);
    }
  }, [clip, sessionActive, mediaReady, anyReady, pipelineHasMedia]);

  useEffect(() => {
    if (!clip || !sessionActive || mediaReady) return;
    const wanted = (startedChannels && startedChannels.length > 0
      ? startedChannels
      : clip.channels.map((c) => c.channel_no).filter((n) => n >= 1));
    if (wanted.length === 0) return;
    if (!wanted.every((ch) => channelDefinitivelyFailed(pipeline, ch))) return;
    setStartError(channelPipelineHint(pipeline, activeChannelRef.current) || PLAYBACK_FAIL_MSG);
    setWaitExpired(true);
    setPlaybackArmed(false);
    setSessionActive(false);
  }, [clip, sessionActive, mediaReady, pipeline, startedChannels]);

  // Do not send a second 0x9201 because the first segments are late. That
  // closed the upload that was about to play and left Front as NONE.

  type RecordingControlResult = {
    ok?: boolean;
    mode?: string;
    outOfFile?: boolean;
    files?: Array<{ channel: number; startAt: string; endAt: string }>;
    playheadAt?: string | null;
  };

  const sendControl = useCallback(
    async (body: Record<string, unknown>): Promise<RecordingControlResult | null> => {
      try {
        return await api<RecordingControlResult>(`/api/devices/${encodeURIComponent(device.id)}/recordings/control`, {
          method: "POST",
          body: JSON.stringify(body),
        });
      } catch {
        /* device may ignore unsupported control */
        return null;
      }
    },
    [device.id],
  );

  // Do not send 0x9202 when the card file starts before the playhead.
  // Control 5 ACKs and does not move the burned-in clock, and firing it as
  // soon as media is ready stalled the upload that had just started.
  useEffect(() => {
    pendingSeekAt.current = null;
  }, [mediaReady]);

  const startRecording = async (next: RecordingClip, startAtIso: string): Promise<boolean> => {
    if (startingRef.current) return false;
    startingRef.current = true;
    const startAbort = new AbortController();
    const startTimer = window.setTimeout(() => startAbort.abort(), START_TIMEOUT_MS);
    try {
    const channelsWanted = next.channels.map((c) => c.channel_no).filter((n) => n >= 1);
    const preferred = activeChannelRef.current;
    const channels =
      channelsWanted.includes(preferred)
        ? [preferred]
        : channelsWanted.length > 0
          ? [channelsWanted[0]!]
          : [];
    if (channels.length === 0) {
      setStreams([]);
      setSessionActive(false);
      setStartedChannelsStable([]);
      setStartError(UNAVAILABLE_MSG);
      setStartingPlayback(false);
      return false;
    }
    if (channels[0] === 1 || channels[0] === 2) {
      setActiveChannel(channels[0]);
      activeChannelRef.current = channels[0];
    }
    // Keep session inactive until /start proves media — avoids /stream storms and
    // "Loading…" with empty HLS while 0x9201 is still being negotiated.
    setStartedChannelsStable(channels);
    setSessionActive(false);
    setPlaybackArmed(false);
    setStartingPlayback(true);
    setStartError(null);
    console.info("[PLAYBACK] command=start", {
      at: new Date().toISOString(),
      camera: device.serial_no,
      channels,
      startAt: startAtIso,
      endAt: next.end_at,
    });
    setWaitExpired(false);
    setRecoverNonce((n) => n + 1);
    // Do not seed windowEnd to the full search clip — that made every scrub look
    // in-file and skipped opening the real 0x1205 card file for that timestamp.
    windowStartRef.current = 0;
    windowEndRef.current = 0;
    const started = await api<RecordingStartResult>(
      `/api/devices/${encodeURIComponent(device.id)}/recordings/start`,
      {
        method: "POST",
        body: JSON.stringify({
          startAt: startAtIso,
          endAt: next.end_at,
          channels,
          speed: speedRef.current,
        }),
        signal: startAbort.signal,
      },
    );
    if (!started.ok) {
      const reason =
        started.mode === "no_file"
          ? UNAVAILABLE_MSG
          : started.message ||
            (started.mode === "offline"
              ? "Camera is offline on JT808 — historical playback needs an active terminal link."
              : started.mode === "not_authenticated"
                ? "Camera JT808 link is not authenticated — wait for login, then Retry."
                : started.mode === "rejected"
                  ? "Camera rejected historical playback."
                  : PLAYBACK_FAIL_MSG);
      setStartError(reason);
      setSessionActive(false);
      setPlaybackArmed(false);
      setStartingPlayback(false);
      setStreams([]);
      setStartedChannelsStable(channels);
      setWaitExpired(true);
      streamPollAbort.current?.abort();
      return false;
    }
    const fileChannels = (started.files ?? []).map((f) => f.channel).filter((n) => n >= 1);
    const active =
      (started.channels ?? []).filter((n) => n >= 1).length > 0
        ? (started.channels ?? []).filter((n) => n >= 1)
        : fileChannels.length > 0
          ? fileChannels
          : channels;
    // Never add channels that are not on this clip (Rear-only must not seed /pb/ch1).
    const clipSet = new Set(channels);
    const seeded = [...new Set(active.filter((n) => clipSet.has(n)).concat(channels))].sort((a, b) => a - b);
    setStartedChannelsStable(active.filter((n) => clipSet.has(n)));
    // /start already confirmed playable /pb HLS for ready channels — seed live
    // so VideoPlayer can attach. Channels not yet ready stay waiting until /stream.
    const readySet = new Set(
      (started.hlsResults ?? [])
        .filter((row) => row.ready)
        .map((row) => row.channel)
        .filter((n) => n >= 1),
    );
    console.info("[PLAYBACK] state=" + (started.warming ? "BUFFERING" : "STARTING"), {
      at: new Date().toISOString(),
      sessionId: started.sessionId,
      hlsGen: started.hlsGen ?? null,
      channels: active,
      warming: Boolean(started.warming),
      files: started.files,
    });
    hlsGenRef.current = started.hlsGen ?? "";
    setHlsGen(started.hlsGen ?? "");
    setStreams(
      seeded.map((channel_no) => ({
        ...streamForPlayback(device.serial_no, channel_no, started),
        live: readySet.has(channel_no),
        waiting: !readySet.has(channel_no),
      })),
    );
    const clipEndMs = Date.parse(next.end_at);
    // Server watchSdFileEnd chains the next SD file. Active 0x1205 bounds are the
    // scrub window — never expand to the full search clip (that skipped file switches).
    applySdFileWindow(started.files);
    prefetchedEndRef.current = 0;
    if (!sdFileWindowMs(started.files)) {
      windowStartRef.current = Number.isFinite(Date.parse(startAtIso)) ? Date.parse(startAtIso) : Date.now();
      windowEndRef.current = Number.isFinite(clipEndMs) ? clipEndMs : windowStartRef.current + PLAY_WINDOW_MS;
    }
    pendingSeekAt.current = null;
    waitStartedAt.current = Date.now();
    setWaitExpired(false);
    setWaitEpoch((n) => n + 1);
    setStartError(null);
    setStartingPlayback(false);
    // Arm only after the API confirmed JT1078 packets — poll /stream then attach HLS.
    setSessionActive(true);
    setPlaybackArmed(true);
    return true;
    } catch (err) {
      const aborted = err instanceof DOMException && err.name === "AbortError";
      setStartError(aborted ? PLAYBACK_FAIL_MSG : err instanceof Error ? err.message : PLAYBACK_FAIL_MSG);
      setSessionActive(false);
      setPlaybackArmed(false);
      setStartingPlayback(false);
      setStreams([]);
      setWaitExpired(true);
      streamPollAbort.current?.abort();
      return false;
    } finally {
      window.clearTimeout(startTimer);
      startingRef.current = false;
    }
  };

  const selectClip = async (next: RecordingClip, playAtIso?: string): Promise<boolean> => {
    setStartError(null);
    const channels = next.channels.map((c) => c.channel_no).filter((n) => n >= 1);
    // Synchronous seed: clip.id remounts VideoPlayer immediately. Hold HLS URLs
    // until /start succeeds so tiles never spin on empty /pb manifests.
    setClip(next);
    setStreams([]);
    setStartedChannelsStable(channels);
    // Keep session inactive until /start succeeds so /stream does not poll early.
    setSessionActive(false);
    setPlaybackArmed(false);
    setStartingPlayback(false);
    setPipeline(null);
    const playAt = playAtForClip(next, playAtIso ?? next.start_at);
    const playAtMs = Date.parse(playAt);
    // Offset is on the Search Window timeline: absoluteTarget = searchStart + offset.
    // Prefer the locked searchWindow (set synchronously on Search); fall back to
    // playAtIso (Search start passed from searchRecordings) when needed.
    const searchOrigin = searchWindowRef.current
      ? Date.parse(searchWindowRef.current.from)
      : searchWindow
        ? Date.parse(searchWindow.from)
        : playAtIso
          ? Date.parse(playAtIso)
          : Date.parse(next.start_at);
    const initialOffset =
      Number.isFinite(playAtMs) && Number.isFinite(searchOrigin) ? Math.max(0, playAtMs - searchOrigin) : 0;
    setOffsetMs(initialOffset);
    offsetRef.current = initialOffset;
    setOutsideRecording(!inActualRecording(playAtMs, next));
    outsideRecordingRef.current = !inActualRecording(playAtMs, next);
    setPlaying(false);
    playingRef.current = false;
    setEnded(false);
    setSeeking(false);
    setSpeed(1);
    speedRef.current = 1;
    setGpsPoints([]);
    setMediaReady(false);
    mediaReadyRef.current = false;
    setMediaBuffering(false);
    mediaBufferingRef.current = false;
    setFrontBuffering(false);
    setRearBuffering(false);
    waitStartedAt.current = 0;
    setWaitExpired(false);
    setMuted(true);
    setFrontStatus("idle");
    setRearStatus("idle");
    retriedStart.current = false;
    extendingRef.current = false;
    window.clearTimeout(rearTimer.current);
    try {
      // Always tear down any prior 0x9201 /pb session before starting the new clip.
      await api(`/api/devices/${encodeURIComponent(device.id)}/recordings/stop`, { method: "POST", body: "{}" }).catch(() => undefined);
      const ok = await startRecording(next, playAt);
      if (!ok) return false;
      const data = await api<{ positions: Position[] }>(
        `/api/devices/${encodeURIComponent(device.id)}/positions?from=${encodeURIComponent(next.start_at)}&to=${encodeURIComponent(next.end_at)}&limit=5000`,
      );
      const chronological = [...(data.positions ?? [])].reverse().map((p) => ({
        lat: p.lat,
        lon: p.lon,
        speed_kmh: p.speed_kmh,
        heading_deg: p.heading_deg,
        recorded_at: p.recorded_at,
      }));
      setGpsPoints(chronological);
      return true;
    } catch (err) {
      setStartError(err instanceof Error ? err.message : PLAYBACK_FAIL_MSG);
      setSessionActive(false);
      setStartingPlayback(false);
      setWaitExpired(true);
      return false;
    }
  };
  selectClipRef.current = selectClip;

  const togglePlay = () => {
    if (!clip || !searchWindow) return;
    if (ended) {
      void replayClip();
      return;
    }
    if (playing) {
      setPlaying(false);
      playingRef.current = false;
      if (mediaReady && !outsideRecording) void sendControl({ action: "pause", channels: [activeChannelRef.current] });
      return;
    }
    const searchStart = Date.parse(searchWindow.from);
    const absoluteMs = searchStart + offsetRef.current;
    if (!inActualRecording(absoluteMs, clip)) {
      setOutsideRecording(true);
      outsideRecordingRef.current = true;
      return;
    }
    setOutsideRecording(false);
    outsideRecordingRef.current = false;
    setPlaying(true);
    playingRef.current = true;
    lastTickRef.current = 0;
    setEnded(false);
    if (mediaReady) {
      void sendControl({ action: "play", speed, channels: [activeChannelRef.current] });
      return;
    }
    const playAt = new Date(absoluteMs).toISOString();
    void startRecording(clip, playAt).then((ok) => {
      if (ok) void sendControl({ action: "play", speed, channels: [activeChannelRef.current] });
    });
  };

  const replayClip = async () => {
    if (!clip || !searchWindow) return;
    setEnded(false);
    const searchStart = Date.parse(searchWindow.from);
    const actualStart = Date.parse(clip.start_at);
    const replayOffset =
      Number.isFinite(searchStart) && Number.isFinite(actualStart) ? Math.max(0, actualStart - searchStart) : 0;
    setOffsetMs(replayOffset);
    offsetRef.current = replayOffset;
    setOutsideRecording(false);
    outsideRecordingRef.current = false;
    setSeeking(false);
    setMediaReady(false);
    mediaReadyRef.current = false;
    setMediaBuffering(false);
    mediaBufferingRef.current = false;
    setFrontBuffering(false);
    setRearBuffering(false);
    setPlaying(false);
    playingRef.current = false;
    setFrontStatus("idle");
    setRearStatus("idle");
    setStartError(null);
    setWaitExpired(false);
    try {
      await api(`/api/devices/${encodeURIComponent(device.id)}/recordings/stop`, { method: "POST", body: "{}" }).catch(() => undefined);
      const ok = await startRecording(clip, clip.start_at);
      if (!ok) return;
      setPlaying(true);
      playingRef.current = true;
      lastTickRef.current = 0;
      void sendControl({ action: "play", speed: speedRef.current, channels: [activeChannelRef.current] });
    } catch (err) {
      setStartError(err instanceof Error ? err.message : PLAYBACK_FAIL_MSG);
    }
  };

  const applySeek = (nextOffset: number) => {
    if (!clip || !searchWindow) return;
    // Timeline = Search Window. absoluteTarget = searchStart + timelinePosition.
    // Never map from video.currentTime or HLS playlist duration.
    const searchStart = Date.parse(searchWindow.from);
    const searchEnd = Date.parse(searchWindow.to);
    const searchDur = Math.max(0, searchEnd - searchStart);
    if (!(searchDur > 0)) return;
    const clamped = Math.max(0, Math.min(searchDur, nextOffset));
    const absoluteMs = searchStart + clamped;
    const seekAt = new Date(absoluteMs).toISOString();
    setEnded(false);
    seekGen.current += 1;
    const gen = seekGen.current;
    // The timeline clock stays on the last decoded picture until the new
    // playlist produces a frame. The slider shows the request separately.
    seekCommitRef.current = clamped;
    setSeekTargetMs(clamped);
    lastTickRef.current = 0;

    // Availability layer: outside actual recording → keep absolute playhead, no
    // fake video, no playback error.
    if (!inActualRecording(absoluteMs, clip)) {
      seekAbort.current?.abort();
      window.clearTimeout(seekTimer.current);
      setOutsideRecording(true);
      outsideRecordingRef.current = true;
      setSeeking(false);
      setStartError(null);
      setPlaybackArmed(false);
      setStreams((prev) => prev.map((row) => ({ ...row, live: false, waiting: true })));
      return;
    }

    setOutsideRecording(false);
    outsideRecordingRef.current = false;
    // Drop the picture before the new playlist exists. PLAYING returns only
    // after the replacement player decodes a frame.
    seekingRef.current = true;
    mediaSecRef.current = -1;
    seekStreamReadyRef.current = false;
    setSeekStreamReady(false);
    setSeeking(true);
    setPlaybackArmed(false);
    setSeekAttach(false);
    setFrontStatus("idle");
    setRearStatus("idle");
    setSeekEpoch((n) => n + 1);
    const channel = activeChannelRef.current;
    const channels = [channel];
    window.clearTimeout(seekTimer.current);
    seekAbort.current?.abort();
    seekTimer.current = window.setTimeout(() => {
      if (gen !== seekGen.current) return;
      const ac = new AbortController();
      seekAbort.current = ac;

      const finishSeek = (started: RecordingStartResult) => {
        if (gen !== seekGen.current || ac.signal.aborted) return;
        if (started.files?.length) applySdFileWindow(started.files);
        prefetchedEndRef.current = 0;
        const nextGen = started.hlsGen ?? "";
        hlsGenRef.current = nextGen;
        setHlsGen(nextGen);
        const readySet = new Set(
          (started.hlsResults ?? [])
            .filter((row) => row.ready)
            .map((row) => row.channel)
            .filter((n) => n >= 1),
        );
        setStreams((prev) => {
          const byCh = new Map(prev.map((row) => [row.channel_no, row]));
          for (const channel_no of channels) {
            const seeded = streamForPlayback(device.serial_no, channel_no, started);
            const live = readySet.has(channel_no);
            byCh.set(channel_no, { ...seeded, live, waiting: !live });
          }
          return [...byCh.values()].sort((a, b) => a.channel_no - b.channel_no);
        });
        const attached = streamForPlayback(device.serial_no, channel, started);
        console.info("[PLAYBACK] attach", {
          at: new Date().toISOString(),
          sessionId: started.sessionId ?? null,
          hlsGen: nextGen || null,
          hlsUrl: attached.hlsUrl,
          requested: seekAt,
          commandStart: started.files?.[0]?.startAt ?? null,
          commandEnd: started.files?.[0]?.endAt ?? null,
        });
        setStartedChannelsStable(channels);
        setStartError(null);
        setSessionActive(true);
        setMediaReady(true);
        mediaReadyRef.current = true;
        // Attach the new playlist, but keep the playhead and the SEEKING
        // state until that player reports a decoded frame. Clear any leftover
        // live status so the previous picture cannot commit the scrub.
        setFrontStatus("connecting");
        setRearStatus("connecting");
        seekStreamReadyRef.current = true;
        setSeekStreamReady(true);
        setPlaybackArmed(true);
        setSeekAttach(false);
      };

      const failSoft = () => {
        if (gen !== seekGen.current || ac.signal.aborted) return;
        seekingRef.current = false;
        seekStreamReadyRef.current = false;
        seekCommitRef.current = null;
        setSeekStreamReady(false);
        setSeekTargetMs(null);
        setSeeking(false);
        setPlaybackArmed(true);
      };

      void (async () => {
        const endAt =
          searchWindowRef.current?.to && Date.parse(searchWindowRef.current.to) > Date.parse(seekAt)
            ? Date.parse(clip.end_at) < Date.parse(searchWindowRef.current.to)
              ? clip.end_at
              : searchWindowRef.current.to
            : clip.end_at;
        console.info("[PLAYBACK] seek", {
          at: new Date().toISOString(),
          seekAt,
          endAt,
          channel,
          relocate: true,
        });
        try {
          const started = await api<RecordingStartResult>(
            `/api/devices/${encodeURIComponent(device.id)}/recordings/start`,
            {
              method: "POST",
              signal: ac.signal,
              body: JSON.stringify({
                startAt: seekAt,
                endAt,
                channels,
                speed: speedRef.current,
                relocate: true,
              }),
            },
          );
          if (gen !== seekGen.current || ac.signal.aborted) return;
          if (!started?.ok || !started.hlsGen) {
            if (started?.mode === "no_file") {
              setPlaybackArmed(false);
              setOutsideRecording(true);
              outsideRecordingRef.current = true;
              setStreams((prev) => prev.map((row) => ({ ...row, live: false, waiting: false })));
              seekingRef.current = false;
              seekStreamReadyRef.current = false;
              seekCommitRef.current = null;
              setSeekStreamReady(false);
              setSeekTargetMs(null);
              setSeeking(false);
              return;
            }
            failSoft();
            return;
          }
          const snappedStart = started.files?.[0]?.startAt;
          const snappedMs = snappedStart ? Date.parse(snappedStart) : Number.NaN;
          if (Number.isFinite(snappedMs) && snappedMs - absoluteMs > 2_000 && searchWindowRef.current) {
            const jumped = snappedMs - Date.parse(searchWindowRef.current.from);
            if (Number.isFinite(jumped) && jumped >= 0) seekCommitRef.current = jumped;
          }
          if (!sdFileWindowMs(started.files) && started.endAt) {
            windowEndRef.current = Date.parse(started.endAt);
          }
          finishSeek(started);
        } catch (err) {
          if (gen !== seekGen.current || ac.signal.aborted) return;
          if (err instanceof DOMException && err.name === "AbortError") return;
          failSoft();
        }
      })();
    }, 120);
  };

  extendSliceRef.current = () => {
    if (extendingRef.current || seekingRef.current) return;
    const search = searchWindowRef.current;
    const sliceEnd = windowEndRef.current;
    if (!search || !(sliceEnd > 0)) return;
    const searchEnd = Date.parse(search.to);
    if (sliceEnd >= searchEnd - 2_000) return;
    extendingRef.current = true;
    const channel = activeChannelRef.current;
    const fromEnd = sliceEnd;
    void api<RecordingStartResult>(`/api/devices/${encodeURIComponent(device.id)}/recordings/start`, {
      method: "POST",
      body: JSON.stringify({
        startAt: new Date(fromEnd).toISOString(),
        endAt: search.to,
        channels: [channel],
        speed: speedRef.current,
        extend: true,
      }),
    })
      .then((started) => {
        const ends = (started?.files ?? []).map((file) => Date.parse(file.endAt)).filter((ms) => Number.isFinite(ms));
        const nextEnd = ends.length ? Math.max(...ends) : 0;
        // Only lengthen the slice. Adopting a later file start made the playhead
        // jump while the burned-in picture was still in the current slice.
        if (started?.ok && nextEnd > windowEndRef.current + 1_000) {
          windowEndRef.current = nextEnd;
          console.info("[PLAYBACK] prefetch", {
            at: new Date().toISOString(),
            from: new Date(fromEnd).toISOString(),
            commandEnd: new Date(nextEnd).toISOString(),
            hlsGen: started.hlsGen ?? null,
            extended: started.extended ?? null,
          });
        }
        const moved = windowEndRef.current > fromEnd + 5_000;
        if (moved) extendingRef.current = false;
        else {
          prefetchedEndRef.current = 0;
          window.setTimeout(() => {
            extendingRef.current = false;
          }, 20_000);
        }
      })
      .catch(() => {
        prefetchedEndRef.current = 0;
        window.setTimeout(() => {
          extendingRef.current = false;
        }, 20_000);
      });
  };

  const changeSpeed = (next: number) => {
    setSpeed(next);
    speedRef.current = next;
    if (playing && !ended) void sendControl({ action: "speed", speed: next, channels: [activeChannelRef.current] });
  };

  const sessionClip = useMemo(() => {
    const from = searchWindow ? Date.parse(searchWindow.from) : list ? Date.parse(list.from) : Number.NaN;
    const to = searchWindow ? Date.parse(searchWindow.to) : list ? Date.parse(list.to) : Number.NaN;
    const clips = (list?.clips ?? []).filter((row) => {
      if (!Number.isFinite(from) || !Number.isFinite(to)) return true;
      const start = Date.parse(row.start_at);
      const end = Date.parse(row.end_at);
      // Strict overlap with the exact search window — never list out-of-range files.
      return end >= from && start <= to;
    });
    return clips[0] ?? null;
  }, [list, searchWindow]);

  // Wanted pane follows the selected camera — not both Front and Rear at once.
  const sourceClip = clip ?? sessionClip;
  const frontWanted = Boolean(sourceClip && clipHasChannel(sourceClip, 1));
  const rearWanted = Boolean(sourceClip && clipHasChannel(sourceClip, 2));
  const activeWanted = activeChannel === 1 ? frontWanted : rearWanted;
  // Hard /start failures (ack_timeout, offline, …) must not keep inventing /pb
  // URLs — that left the tile on "Loading…" and flooded index.m3u8 404s.
  const startHardFailed = Boolean(startError) && (waitExpired || !startingPlayback);
  const playerStatus = activeChannel === 1 ? frontStatus : rearStatus;
  const activeFailed =
    activeWanted &&
    !seeking &&
    !outsideRecording &&
    playerStatus !== "live" &&
    !startingPlayback &&
    (startHardFailed || (channelDefinitivelyFailed(pipeline, activeChannel) && waitExpired));
  const noRecordingForChannel = Boolean(clip || sessionClip) && !activeWanted;
  const pipeRow = pipeline?.[String(activeChannel)] as { packets?: number; hlsHasSegments?: boolean } | undefined;
  const gotPackets = Number(pipeRow?.packets ?? 0) > 0;
  const hlsReady = Boolean(pipeRow?.hlsHasSegments);
  const stateSentence = !hasSearched
    ? ""
    : noRecordingForChannel || outsideRecording
      ? UNAVAILABLE_MSG
      : activeFailed
        ? "Playback failed"
        : ended
          ? "Playback completed"
          : seeking
            ? PREPARING_MSG
            : playerStatus === "live" && !playing
              ? "Paused"
              : playerStatus === "live"
                ? "Playing"
                : startingPlayback
                  ? STARTING_MSG
                  : playbackArmed && hlsReady
                    ? READY_MSG
                    : playbackArmed && !gotPackets
                      ? WAITING_MSG
                      : playbackArmed
                        ? PREPARING_MSG
                        : "";
  const phaseLabel = !hasSearched
    ? "SEARCHING"
    : noRecordingForChannel || outsideRecording
      ? "NONE"
      : activeFailed
        ? "FAILED"
        : ended
          ? "COMPLETED"
          : seeking
            ? "PREPARING"
            : playerStatus === "live" && !playing
              ? "PAUSED"
              : playerStatus === "live"
                ? "PLAYING"
                : startingPlayback
                  ? "REQUESTING"
                  : playbackArmed
                    ? hlsReady
                      ? "READY"
                      : "PREPARING"
                    : "IDLE";
  // Seed /pb URLs only while a start is in flight or armed — never after failure.
  const allowPlaybackUrl =
    (sessionActive || playbackArmed) && !startHardFailed && !noRecordingForChannel && !outsideRecording;
  const activeStream =
    activeWanted && !activeFailed && allowPlaybackUrl
      ? streams.find((c) => c.channel_no === activeChannel && c.hlsUrl) ??
        (playbackArmed ? playbackStream(device.serial_no, activeChannel, hlsGen) : streams.find((c) => c.channel_no === activeChannel) ?? null)
      : null;
  const unavailable = (channel_no: number): ChannelStream => ({
    channel_no,
    mediamtx_path: "",
    hlsUrl: "",
    webrtcUrl: "",
    has_audio: false,
    live: false,
    waiting: true,
  });
  const switchCamera = (channel: 1 | 2) => {
    if (channel === activeChannelRef.current) return;
    setActiveChannel(channel);
    activeChannelRef.current = channel;
    setFrontStatus("idle");
    setRearStatus("idle");
    setFrontBuffering(false);
    setRearBuffering(false);
    setMediaReady(false);
    mediaReadyRef.current = false;
    setStartError(null);
    setWaitExpired(false);
    setSeeking(false);
    if (!clip) return;
    if (!clipHasChannel(clip, channel)) {
      setSessionActive(false);
      setPlaybackArmed(false);
      setStartingPlayback(false);
      setStreams([]);
      setStartError(UNAVAILABLE_MSG);
      setWaitExpired(true);
      return;
    }
    const playAt = new Date(
      (searchWindowRef.current ? Date.parse(searchWindowRef.current.from) : Date.parse(clip.start_at)) +
        offsetRef.current,
    ).toISOString();
    void startRecording(clip, playAt).then((ok) => {
      if (ok && playingRef.current) {
        void sendControl({ action: "play", speed: speedRef.current, channels: [channel] });
      }
    });
  };

  const listBusy = loadingList || cameraSyncing;
  const emptyListMessage = (() => {
    if (!hasSearched) return "Set Start Date, Start Time, End Date, and End Time, then click Search.";
    if (list?.source === "offline") return "Camera is offline on JT808. Confirm live GPS/video is up, then click Search.";
    if (list?.source === "timeout") return "Camera did not answer in time. Click Search to retry.";
    if (list?.source === "unsupported") return "Camera rejected the recording-list command (0x9205).";
    if (list?.source === "error") return list.message || "Recording query failed. Click Search to retry.";
    return NO_RECORDING_MSG;
  })();

  return (
    <section className="playback-panel" aria-label="Recorded playback">
      <header className="playback-head">
        <div>
          <strong>Recorded playback</strong>
          <span>{device.label || device.serial_no}</span>
        </div>
        <button type="button" className="ghost" onClick={onClose}>
          {closeLabel}
        </button>
      </header>

      <div className="playback-toolbar">
        <label>
          Start Date
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>
          Start Time
          <input type="time" step={60} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
        </label>
        <label>
          End Date
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
        <label>
          End Time
          <input type="time" step={60} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
        </label>
        <button type="button" className="primary" onClick={() => void searchRecordings()} disabled={loadingList}>
          {loadingList ? "Searching…" : "Search"}
        </button>
        {cameraSyncing && !loadingList ? <span className="playback-source">Updating from camera storage…</span> : null}
        {loadingList ? <span className="playback-source">Loading indexed recordings…</span> : null}
        {!listBusy && hasSearched && list?.source === "cache" ? <span className="playback-source">Showing last known list from this camera</span> : null}
        {!listBusy && hasSearched && list?.source === "device" ? <span className="playback-source">Loaded from camera storage</span> : null}
        {!listBusy && hasSearched && list?.source === "timeout" && !sessionClip ? (
          <span className="playback-source">Camera timed out — click Search to retry</span>
        ) : null}
        {!listBusy && hasSearched && list?.source === "offline" ? <span className="playback-source">Camera is not connected on JT808</span> : null}
        {!listBusy && hasSearched && list?.source === "unsupported" ? <span className="playback-source">Camera rejected 0x9205</span> : null}
        {!listBusy && hasSearched && list?.source === "error" ? <span className="playback-source">Recording query failed</span> : null}
      </div>
      {listError ? (
        <p className="form-error">
          {listError}{" "}
          <button type="button" className="ghost" onClick={() => void searchRecordings()} disabled={loadingList}>
            Retry
          </button>
        </p>
      ) : null}
      {startError ? (
        <p className="form-error">
          {startError}{" "}
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setStartError(null);
              setWaitExpired(false);
              void searchRecordings();
            }}
            disabled={loadingList}
          >
            Retry
          </button>
        </p>
      ) : null}

      <div className="playback-clips" role="list">
        {loadingList && !sessionClip ? <p className="empty-grid">Loading indexed recordings…</p> : null}
        {!loadingList && !sessionClip ? (
          <div className="empty-grid">
            <p>{emptyListMessage}</p>
            {hasSearched && (list?.source === "timeout" || list?.source === "error" || list?.source === "offline") ? (
              <button type="button" className="primary" onClick={() => void searchRecordings()}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}
        {sessionClip && (searchWindow || list) ? (
          <div className="playback-day">
            <h3>
              {device.label || device.serial_no}
              {" · Search window "}
              {formatWhen((searchWindow ?? list)!.from)} – {formatWhen((searchWindow ?? list)!.to)}
              {durationMs > 0 ? ` (${formatClock(durationMs)})` : ""}
            </h3>
            <p className="playback-clip-meta">
              Actual recording {formatWhen(sessionClip.start_at)} – {formatWhen(sessionClip.end_at)}
              {" · "}
              {formatClock(sessionClip.duration_sec * 1000)} available
              {" · "}
              {clipHasChannel(sessionClip, 1) && clipHasChannel(sessionClip, 2)
                ? "Front + Rear"
                : clipHasChannel(sessionClip, 1)
                  ? "Front"
                  : clipHasChannel(sessionClip, 2)
                    ? "Rear"
                    : "—"}
            </p>
          </div>
        ) : null}
      </div>

      {hasSearched && (clip || sessionClip) ? (
        <>
          <div className="playback-camera-switch" role="group" aria-label="Camera">
            <button
              type="button"
              className={activeChannel === 1 ? "is-on" : ""}
              aria-pressed={activeChannel === 1}
              onClick={() => switchCamera(1)}
            >
              Front
            </button>
            <button
              type="button"
              className={activeChannel === 2 ? "is-on" : ""}
              aria-pressed={activeChannel === 2}
              onClick={() => switchCamera(2)}
            >
              Rear
            </button>
          </div>
          <p className="playback-source">
            Only one recorded playback can run at a time on this camera. Starting Front or Rear stops the other recording first.
          </p>
          <div className={`device-card-cams count-1 playback-cams${seeking ? " is-seeking" : ""}${seekStreamReady ? " is-seek-ready" : ""}`}>
            <VideoPlayer
              key={`pb-${(clip ?? sessionClip)!.id}-ch${activeChannel}-${hlsGen || "base"}-${seekEpoch}`}
              deviceLabel={channelLabel(activeChannel)}
              stream={seeking && !seekStreamReady ? unavailable(activeChannel) : (activeStream ?? unavailable(activeChannel))}
              deviceOnline={Boolean(clip) && activeWanted && !activeFailed && !noRecordingForChannel && !outsideRecording}
              selected
              delaySec={1}
              embedded
              playback
              playbackArmed={playbackArmed && !outsideRecording && !activeFailed && !(seeking && !seekStreamReady)}
              playbackUnavailable={noRecordingForChannel}
              phaseLabel={phaseLabel}
              muted={muted}
              recoverNonce={recoverNonce}
              seekNonce={seekNonce}
              seekAttach={seekAttach}
              recordingOriginMs={Number.isFinite(searchStartMs) ? searchStartMs : 0}
              masterOffsetMs={offsetMs}
              absoluteClockIso={
                Number.isFinite(pictureMs) && pictureMs > 0 ? new Date(pictureMs).toISOString() : undefined
              }
              onPlaybackMedia={(sec) => {
                mediaSecRef.current = sec;
              }}
              playbackPaused={!playing || syncHold || outsideRecording}
              playbackRate={speed}
              playingBadge="REC"
              hideDelay
              onStatusChange={activeChannel === 1 ? setFrontStatus : setRearStatus}
              onBufferingChange={activeChannel === 1 ? setFrontBuffering : setRearBuffering}
              exportClip={
                activeWanted && !activeFailed && !outsideRecording && clip
                  ? exportSpec(device, clip, activeChannel)
                  : undefined
              }
              emptyLabel={
                noRecordingForChannel
                  ? UNAVAILABLE_MSG
                  : outsideRecording
                    ? UNAVAILABLE_MSG
                    : seeking
                      ? PREPARING_MSG
                      : activeFailed
                        ? startError || "Playback failed"
                        : stateSentence || STARTING_MSG
              }
              onSelect={() => undefined}
            />
          </div>

          <div className="playback-controls">
            <button type="button" className="primary" onClick={togglePlay}>
              {ended ? "Replay" : playing ? "Pause" : "Play"}
            </button>
            <button
              type="button"
              className={`mute-btn audio-btn ${muted ? "is-muted" : "is-on"}`}
              onClick={() => setMuted((m) => !m)}
              title={muted ? "Unmute recorded audio" : "Mute recorded audio"}
              aria-label={muted ? "Unmute recorded audio" : "Mute recorded audio"}
              aria-pressed={!muted}
            >
              {muted ? "Unmute" : "Mute"}
            </button>
            <div className="playback-scrub">
              {actualBandWidthPct > 0 ? (
                <div
                  className="playback-scrub-available"
                  style={{ left: `${actualBandLeftPct}%`, width: `${actualBandWidthPct}%` }}
                  title="Actual recording availability"
                  aria-hidden
                />
              ) : null}
              <input
                type="range"
                min={0}
                max={Math.max(1, durationMs)}
                step={250}
                value={Math.min(seeking && seekTargetMs != null ? seekTargetMs : offsetMs, Math.max(1, durationMs))}
                aria-label="Seek within search window"
                disabled={!clip && !sessionClip}
                onInput={(e) => applySeek(Number((e.target as HTMLInputElement).value))}
                onChange={(e) => applySeek(Number(e.target.value))}
              />
            </div>
            <div className="playback-times">
              <span title="Position on Search timeline">{formatClock(offsetMs)}</span>
              <span className="playback-times-range">
                {searchWindow ? (
                  <span className="playback-times-search">
                    Search {formatWhen(searchWindow.from)} – {formatWhen(searchWindow.to)}
                  </span>
                ) : null}
                {clip ? (
                  <span className="playback-times-actual">
                    Recording {formatWhen(clip.start_at)} – {formatWhen(clip.end_at)}
                    {actualDurationMs > 0 ? ` · ${formatClock(actualDurationMs)} available` : ""}
                  </span>
                ) : null}
              </span>
              <span title="Search window duration (timeline max)">
                {formatClock(durationMs)}
              </span>
            </div>
            {Number.isFinite(playheadMs) && playheadMs > 0 ? (
              <p className="playback-playhead-abs" aria-live="polite">
                {outsideRecording ? "No recording at " : "Playhead "}
                {formatWhen(new Date(pictureMs).toISOString())}
                {seeking && seekTargetMs != null
                  ? ` · Seeking to ${formatWhen(
                      new Date(
                        (Number.isFinite(searchStartMs) ? searchStartMs : 0) + seekTargetMs,
                      ).toISOString(),
                    )}…`
                  : ""}
              </p>
            ) : null}
            <div className="playback-speeds" role="group" aria-label="Playback speed">
              {SPEEDS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={speed === value ? "is-on" : ""}
                  disabled={ended}
                  onClick={() => changeSpeed(value)}
                >
                  {value}×
                </button>
              ))}
            </div>
          </div>
          {seeking && seekTargetMs != null ? (
            <p className="playback-gap">
              Seeking to{" "}
              {formatWhen(
                new Date((Number.isFinite(searchStartMs) ? searchStartMs : 0) + seekTargetMs).toISOString(),
              )}
              …
            </p>
          ) : null}
          {outsideRecording && !seeking ? (
            <p className="playback-gap">
              No recording at {formatWhen(new Date(playheadMs).toISOString())} — drag into the available band
              {clip ? ` (${formatWhen(clip.start_at)} – ${formatWhen(clip.end_at)})` : ""}.
            </p>
          ) : null}
          {stateSentence ? (
            <p className="playback-source" aria-live="polite">
              {stateSentence}
              {ended && !outsideRecording ? " — click Replay to watch again." : ""}
            </p>
          ) : null}
          {syncHold && !seeking ? (
            <p className="playback-gap">Buffering — holding the recording timeline…</p>
          ) : null}
          {gps?.gap ? <p className="playback-gap">GPS gap — holding last valid position (no interpolated movement).</p> : null}
          {embedMap ? <PlaybackMap position={gps} trail={trail} gap={Boolean(gps?.gap)} /> : null}
        </>
      ) : (
        <p className="empty-grid">
          {hasSearched
            ? emptyListMessage
            : "Set Start Date, Start Time, End Date, and End Time, then click Search to load recordings."}
        </p>
      )}
    </section>
  );
}
